import React from 'react';
import clsx from 'clsx';

export interface TabItem {
  value: string;
  label: React.ReactNode;
  disabled?: boolean;
}

export interface TabsProps {
  items: TabItem[];
  value: string;
  onChange: (value: string) => void;
  /** Accessible label for the tablist. Required for a11y. */
  'aria-label': string;
  className?: string;
}

export const Tabs: React.FC<TabsProps> = ({
  items,
  value,
  onChange,
  'aria-label': ariaLabel,
  className,
}) => {
  const tablistRef = React.useRef<HTMLDivElement | null>(null);

  const handleKeyDown = (event: React.KeyboardEvent<HTMLButtonElement>) => {
    if (!['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(event.key)) return;
    event.preventDefault();
    const enabledItems = items.filter((i) => !i.disabled);
    const currentIndex = enabledItems.findIndex((i) => i.value === value);
    let nextIndex = currentIndex;
    if (event.key === 'ArrowLeft') {
      nextIndex = currentIndex > 0 ? currentIndex - 1 : enabledItems.length - 1;
    } else if (event.key === 'ArrowRight') {
      nextIndex = currentIndex < enabledItems.length - 1 ? currentIndex + 1 : 0;
    } else if (event.key === 'Home') {
      nextIndex = 0;
    } else if (event.key === 'End') {
      nextIndex = enabledItems.length - 1;
    }
    onChange(enabledItems[nextIndex].value);
  };

  return (
    <div
      ref={tablistRef}
      role="tablist"
      aria-label={ariaLabel}
      className={clsx(
        'flex gap-1 border-b border-white/[0.08]',
        className,
      )}
    >
      {items.map((item) => {
        const isActive = item.value === value;
        return (
          <button
            key={item.value}
            type="button"
            role="tab"
            aria-selected={isActive}
            aria-disabled={item.disabled || undefined}
            tabIndex={isActive ? 0 : -1}
            disabled={item.disabled}
            onClick={() => !item.disabled && onChange(item.value)}
            onKeyDown={handleKeyDown}
            className={clsx(
              '-mb-px px-3.5 py-2.5 text-[13px] border-b-2 transition-colors duration-150',
              'focus:outline-none focus-visible:ring-2 focus-visible:ring-accent focus-visible:ring-offset-2 focus-visible:ring-offset-surface-1',
              'disabled:opacity-50 disabled:cursor-not-allowed',
              isActive
                ? 'text-accent border-accent'
                : 'text-slate-400 border-transparent hover:text-slate-200',
            )}
          >
            {item.label}
          </button>
        );
      })}
    </div>
  );
};

export default Tabs;
